import Swal from 'sweetalert2'

let authRecoveryInProgress = false

/** ตัดการเชื่อมต่อ WebSocket เพื่อให้ gateway แจ้ง user_offline ทันที */
function disconnectWebSocketNow(): void {
  if (typeof window === 'undefined') return
  try {
    const win = window as unknown as {
      __huayWebSocket?: {
        disconnect?: () => void
        removeAllListeners?: () => void
        close?: () => void
      } | null
    }
    if (win.__huayWebSocket) {
      win.__huayWebSocket.removeAllListeners?.()
      win.__huayWebSocket.disconnect?.()
      win.__huayWebSocket.close?.()
      win.__huayWebSocket = null
    }
  } catch {
    /* ignore */
  }
}

function clearAuthStorage(): void {
  ;[localStorage, sessionStorage].forEach((storage) => {
    storage.removeItem('accessToken')
    storage.removeItem('refreshToken')
    storage.removeItem('deviceId')
    storage.removeItem('user')
    storage.removeItem('tenantId')
    storage.removeItem('rememberMe')
  })
  sessionStorage.removeItem('concurrentLoginDetected')
}

/** HashRouter: ไปหน้า login แล้ว reload เพื่อล้าง state */
function redirectToLogin(): void {
  if (typeof window === 'undefined') return
  window.location.hash = '#/login'
  window.location.reload()
}

/**
 * เมื่อ backend ส่ง 401 + code SESSION_REPLACED (มีการล็อกอินจากที่อื่น)
 * แสดง SweetAlert นับถอยหลัง 5 วินาที (ห้ามกดปิด) จากนั้นล้าง auth และไป /login
 */
export async function handleSessionReplaced(): Promise<void> {
  if (authRecoveryInProgress) return
  authRecoveryInProgress = true

  let countDown = 5
  await Swal.fire({
    title: 'บัญชีถูกใช้งานจากอุปกรณ์อื่น',
    html: `
      <div class="modern-session-swal-body">
        <div class="modern-session-swal-icon-wrap modern-session-swal-icon-wrap--danger">
          <div class="modern-session-swal-icon modern-session-swal-icon--danger">!</div>
        </div>
        <p class="modern-session-swal-message">
          มีการเข้าสู่ระบบบัญชีนี้จากอุปกรณ์หรือแท็บอื่น เพื่อความปลอดภัย ระบบจะพาคุณไปหน้าเข้าสู่ระบบอัตโนมัติ
        </p>
        <div class="modern-session-swal-timer-card modern-session-swal-timer-card--danger">
          <div id="session-replaced-countdown" class="modern-session-swal-timer-value">${countDown}</div>
          <div class="modern-session-swal-timer-caption">วินาทีก่อนออกจากระบบ</div>
        </div>
      </div>
    `,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    timer: 5000,
    timerProgressBar: true,
    backdrop: 'rgba(2, 6, 23, 0.86)',
    customClass: {
      popup: 'modern-session-swal-popup modern-session-swal-popup--danger',
      title: 'modern-session-swal-title',
      htmlContainer: 'modern-session-swal-html',
    },
    didOpen: () => {
      const el = document.getElementById('session-replaced-countdown')
      if (!el) return
      const iv = setInterval(() => {
        countDown -= 1
        el.textContent = String(Math.max(0, countDown))
        if (countDown <= 0) clearInterval(iv)
      }, 1000)
    },
  })

  disconnectWebSocketNow()
  clearAuthStorage()
  try {
    sessionStorage.setItem('htd_auth_notice', 'replaced')
  } catch {
    /* ignore */
  }
  redirectToLogin()
}

/**
 * เมื่อ token หมดอายุ / หลุดจากระบบ (401/403 invalid token)
 * ล้าง auth แจ้งเตือน แล้วพาไปหน้า login ทันที — ไม่ค้างหน้าเดิม
 * (แนวเดียวกับ user-classic handleAuthExpired)
 */
export async function handleAuthExpired(): Promise<void> {
  if (authRecoveryInProgress) return
  authRecoveryInProgress = true

  disconnectWebSocketNow()
  clearAuthStorage()

  try {
    sessionStorage.setItem('htd_auth_notice', 'expired')
  } catch {
    /* ignore */
  }

  await Swal.fire({
    icon: 'warning',
    title: 'เซสชันหมดอายุ',
    text: 'กรุณาเข้าสู่ระบบใหม่อีกครั้ง',
    confirmButtonText: 'ตกลง',
    allowOutsideClick: false,
    allowEscapeKey: false,
    confirmButtonColor: 'var(--htd-primary)',
  })

  redirectToLogin()
}

/** helper ตรวจว่า response เป็น auth หมดอายุหรือไม่ */
export function isAuthExpiredResponse(status: number, responseData?: unknown): boolean {
  if (status === 401) return true
  if (status !== 403) return false
  const data = responseData as { error?: string; message?: string } | null | undefined
  const message = String(data?.error || data?.message || '').toLowerCase()
  return (
    message.includes('invalid or expired token') ||
    message.includes('invalid token') ||
    message.includes('expired token') ||
    message.includes('access token required')
  )
}

/**
 * จัดการ 401/403 จาก API กลาง ๆ
 * @returns true ถ้าได้ trigger redirect/logout แล้ว (caller ควรหยุดต่อ)
 */
export async function handleUnauthorizedResponse(
  status: number,
  responseData?: unknown
): Promise<boolean> {
  const data = responseData as { code?: string } | null | undefined
  if (status === 401 && data?.code === 'SESSION_REPLACED') {
    await handleSessionReplaced()
    return true
  }
  if (isAuthExpiredResponse(status, responseData)) {
    await handleAuthExpired()
    return true
  }
  return false
}
